import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Bike, Bus, Car, CarFront, Cog, Crown, Gauge, Mountain, Plane, Ship, Sun, TrainFront, Truck, Users, Zap, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/** Mobile-only (< lg) Airbnb-style filter groups for the full filter sheet. */

export function carTypeIcon(slugOrLabel: string): ReactNode {
  const s = slugOrLabel.toLowerCase();
  const cls = "h-6 w-6";
  if (/aeroplane|airplane|aircraft/.test(s)) return <Plane className={cls} aria-hidden />;
  if (/boat|yacht/.test(s)) return <Ship className={cls} aria-hidden />;
  if (/motorcycle|motorbike/.test(s)) return <Bike className={cls} aria-hidden />;
  if (/train|tram/.test(s)) return <TrainFront className={cls} aria-hidden />;
  if (/electric|hybrid/.test(s)) return <Zap className={cls} aria-hidden />;
  if (/convertible|roadster|cabrio|spider|spyder/.test(s)) return <Sun className={cls} aria-hidden />;
  if (/truck|pick-?up|ute/.test(s)) return <Truck className={cls} aria-hidden />;
  if (/van|bus|camper|minibus/.test(s)) return <Bus className={cls} aria-hidden />;
  if (/suv|4x4|off-?road|land/.test(s)) return <Mountain className={cls} aria-hidden />;
  if (/limo|luxury|rolls|wedding/.test(s)) return <Crown className={cls} aria-hidden />;
  if (/sport|coupe|gt|muscle|race/.test(s)) return <Gauge className={cls} aria-hidden />;
  if (/saloon|sedan|estate|hatch/.test(s)) return <CarFront className={cls} aria-hidden />;
  return <Car className={cls} aria-hidden />;
}

export function featureIcon(value: string): ReactNode {
  const cls = "h-6 w-6";
  if (value === "instant") return <Zap className={cls} aria-hidden />;
  if (value === "convertible") return <Sun className={cls} aria-hidden />;
  if (value === "automatic") return <Cog className={cls} aria-hidden />;
  return <Car className={cls} aria-hidden />;
}

export const seatsIcon = <Users className="h-4 w-4" aria-hidden />;

export interface TileItem {
  value: string;
  label: string;
  sublabel?: string;
  icon: ReactNode;
  selected: boolean;
  onToggle: () => void;
}

export function MobileTileGroup({ title, items, initialCount = 6 }: { title: string; items: TileItem[]; initialCount?: number }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // Always keep selected tiles visible even when collapsed.
  const collapsed = items.filter((it, i) => i < initialCount || it.selected);
  const visible = expanded ? items : collapsed;
  const hidden = items.length - collapsed.length;
  return (
    <section className="msf-group">
      <h3 className="msf-heading">{title}</h3>
      <div className="mt-3 grid grid-cols-3 gap-2.5">
        {visible.map((it) => (
          <button
            key={it.value}
            type="button"
            aria-pressed={it.selected}
            onClick={it.onToggle}
            className={cn("msf-tile flex min-h-[92px] flex-col items-start justify-between gap-2 rounded-2xl p-3 text-left", it.selected && "is-selected")}
          >
            <span className="msf-tile-icon">{it.icon}</span>
            <span className="w-full">
              <span className="block text-[0.8125rem] font-semibold leading-tight text-foreground [overflow-wrap:anywhere]">{it.label}</span>
              {it.sublabel && <span className="mt-0.5 block text-[0.6875rem] text-muted-foreground">{it.sublabel}</span>}
            </span>
          </button>
        ))}
      </div>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-3 inline-flex min-h-11 items-center gap-1 text-[0.9375rem] font-semibold text-foreground underline underline-offset-4"
        >
          {expanded ? t("search.mobile.show_less", { defaultValue: "Show less" }) : t("search.mobile.show_more", { defaultValue: "Show more" })}
          <ChevronDown className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")} aria-hidden />
        </button>
      )}
    </section>
  );
}

export function MobileChipGroup({
  title,
  items,
  segmented,
  icon,
}: {
  title: string;
  items: Array<{ value: string; label: string; selected: boolean; onToggle: () => void }>;
  segmented?: boolean;
  icon?: ReactNode;
}) {
  return (
    <section className="msf-group">
      <h3 className="msf-heading flex items-center gap-2">{icon}{title}</h3>
      <div className={cn("mt-3", segmented ? "msf-segmented grid rounded-2xl p-1" : "flex flex-wrap gap-2")} style={segmented ? { gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` } : undefined}>
        {items.map((it) => (
          <button
            key={it.value}
            type="button"
            aria-pressed={it.selected}
            onClick={it.onToggle}
            className={cn(
              segmented ? "msf-seg min-h-[44px] rounded-xl text-sm font-medium" : "msf-chip min-h-[40px] rounded-full px-4 text-sm font-medium",
              it.selected && "is-selected",
            )}
          >
            {it.label}
          </button>
        ))}
      </div>
    </section>
  );
}
